import * as React from 'react'
import i18next from 'i18next'
import classNames from 'classnames'

import { abbreviateFileSize } from '../../../../shared/lib/fileSize'
import { hasRawAnswer } from '../../../../shared/domain/reports/types.no-deps'
import { assumeExhaustion } from '../../../../shared/lib/typeHelpers.no-deps'
import moment from 'moment'
import { MediaGallery } from './MediaGallery'
import { renderPromise } from '../../util/PromiseRenderer'
import { SVG_File } from '../../svg/SVG_File'
import type { FileMetaApp } from '../../../../shared/domain/files'
import { UnitChips } from '../../unit/UnitChips'
import type { AppUnitType, UnitData } from '../../../../shared/domain/units/types.no-deps'
import { isUnitArray } from './isUnitArray'
import { isNotNil } from '../../../../shared/lib/functional/nil.no-deps'
import type { SharedProps } from './SharedProps'
import { withS3Url } from '../../file/withS3Url'
import { ExtensibleFileAnswer } from './ExtensibleFileAnswer'
import { TextContent } from '../../flow/ElementTextContent'
import {
    type FlowItem,
    type AnswerFlowItem,
    type ElementFlowItem,
    isAnswerFlowItem,
} from '../../../../shared/domain/viewableReports/types.no-deps'
import { IconButton } from '../../ui/IconButton'
import AnswerTimeline from '../../../../shared/webcomponents/react-wrappers/AnswerTimeline'
import { elementDomId } from '../../flow/element'
import { elementIdReferencedByItem } from '../../../../shared/domain/viewableReports/viewablePageReport.no-deps'

interface Props extends SharedProps {
    item: FlowItem
    unitData?: UnitData
}

interface State {
    isHistoryShown: boolean
}

export class ViewFlowItem extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props)

        this.state = {
            isHistoryShown: false,
        }
    }

    private onClickHistoryButton = () => {
        this.setState({ isHistoryShown: !this.state.isHistoryShown })
    }

    private getAnswerHistory() {
        const { item, flowAnswers } = this.props

        if (!isAnswerFlowItem(item) || !hasRawAnswer(item.answer.value) || !flowAnswers) {
            return []
        }

        const { rawAnswer } = item.answer.value

        return this.props.flowAnswers.filter(
            (flowAnswer) => flowAnswer.element === rawAnswer.element && flowAnswer.loopPath === rawAnswer.loopPath
        )
    }

    private renderAnswerIcon = (item: AnswerFlowItem) => {
        const answerValue = item.answer.value
        switch (answerValue.type) {
            case 'boolean':
                return <zaptic-icon icon-name="element-yes-no" />
            case 'calculation':
                return <zaptic-icon icon-name="element-calculation" />
            case 'date':
                return <zaptic-icon icon-name="element-date-time" />
            case 'extensible-files':
                return <zaptic-icon icon-name="element-camera" />
            case 'files':
                return <zaptic-icon icon-name="element-files" />
            case 'guide':
                return <zaptic-icon icon-name="element-guide" />
            case 'number':
                return <zaptic-icon icon-name="element-number" />
            case 'photos':
                return <zaptic-icon icon-name="element-camera" />
            case 'reports':
                return <zaptic-icon icon-name="element-report" />
            case 'section':
                // Sections shouldn't be displayed in answers, and filtered out before we get this far
                throw new Error('Unhandled Answer')
            case 'select':
                return <zaptic-icon icon-name="element-select" />
            case 'string':
                return <zaptic-icon icon-name="element-text-capture" />
            case 'units':
                return <zaptic-icon icon-name="element-units" />
            default:
                assumeExhaustion(answerValue)
        }
    }

    private renderElementIcon = (item: ElementFlowItem) => {
        const { element } = item

        switch (element.type) {
            case 'text-content':
                return <zaptic-icon icon-name="element-text-content" />
            default:
                assumeExhaustion(element.type)
        }
    }

    private renderIcon = () => {
        const { item } = this.props

        switch (item.type) {
            case 'answer':
                return this.renderAnswerIcon(item)
            case 'element':
                return this.renderElementIcon(item)
            default:
                assumeExhaustion(item)
        }
    }

    private renderAnswerContent = (item: AnswerFlowItem) => {
        const answerValue = item.answer.value

        switch (answerValue.type) {
            case 'boolean':
                return (
                    <div className="AppCore__report__ViewAnswer__Boolean">
                        {answerValue.value ? i18next.t('appcore:common.yes') : i18next.t('appcore:common.no')}
                    </div>
                )
            case 'calculation':
                return <div className="AppCore__report__ViewAnswer__Calculation">{answerValue.value}</div>
            case 'date':
                return (
                    <div className="AppCore__report__ViewAnswer__Date posix-answer">
                        {moment(answerValue.value).format('D/M/YYYY hh:mm A')}
                    </div>
                )
            case 'extensible-files':
                return (
                    <ExtensibleFileAnswer
                        deps={this.props.deps}
                        answerValue={answerValue}
                        photoUrls={this.props.photoUrls}
                        allowZoom={this.props.allowImageZoom}
                    />
                )
            case 'files':
                const elementsPromise = this.props.deps.httpClient
                    .getFileMetaBulk({ fileIds: answerValue.value })
                    .then((output) => output.fileMeta)
                    .then((metaItems) => metaItems.map(toElementFromFileMetaFn))

                const toElementFromFileMetaFn = (fileMeta: FileMetaApp): JSX.Element => {
                    const fileName = fileMeta.fileName
                    return withS3Url(this.props.deps, fileMeta.fetchUrl, ({ fullSizeS3Url }) => (
                        <li className="AppCore__report__ViewAnswer__File" key={fileMeta.fileId}>
                            <div
                                className="AppCore__report__ViewAnswer__FileContent"
                                onClick={() => this.props.onOpenFile(fullSizeS3Url, fileMeta.extension, fileName)}
                            >
                                <div className="AppCore__report__ViewAnswer__FileIcon">
                                    <SVG_File extension={fileMeta.extension} />
                                </div>
                                <div className="AppCore__report__ViewAnswer__FileDetails">
                                    <div className="AppCore__report__ViewAnswer__FileName">
                                        <zaptic-ellipsis>{fileName}</zaptic-ellipsis>
                                    </div>
                                    <div className="AppCore__report__ViewAnswer__FileSize">
                                        {abbreviateFileSize(fileMeta.fileSizeInBytes)}
                                    </div>
                                </div>
                            </div>
                        </li>
                    ))
                }
                return renderPromise(
                    this.props.deps,
                    'files-select-elements',
                    () => elementsPromise,
                    (elements) => <ul className="AppCore__report__ViewAnswer__Files">{elements}</ul>
                )
            case 'guide':
                // There is no content for a guide element
                return null
            case 'number':
                return <div className="AppCore__report__ViewAnswer__Number">{answerValue.value}</div>
            case 'photos':
                return (
                    <MediaGallery>
                        {answerValue.value.map((file) =>
                            withS3Url(this.props.deps, file, ({ fullSizeS3Url }) => {
                                return (
                                    <zaptic-annotated-image
                                        key={fullSizeS3Url}
                                        className="AppCore__report__ViewAnswer__Image"
                                        src={fullSizeS3Url}
                                    />
                                )
                            })
                        )}
                    </MediaGallery>
                )
            case 'reports':
                const reportCountText =
                    answerValue.value.length === 1
                        ? `1 ${i18next.t('appcore:common.report')}`
                        : `${answerValue.value.length} ${i18next.t('appcore:common.reports')}`

                return (
                    <>
                        <span className="AppCore__report__ViewAnswer__ReportCount">{reportCountText}</span>
                        <ul className="AppCore__report__ViewAnswer__Reports">
                            {answerValue.value.map((reportId) => {
                                const details = this.props.reportDetails[reportId]

                                if (details == null) return null

                                const subject = this.props.units.find((unit) => unit.id === details.unitSubjectId)

                                return (
                                    <li>
                                        <a
                                            className="AppCore__report__ViewAnswer__Report"
                                            onClick={() => this.props.onReportPostClick(details.postId, details)}
                                        >
                                            <zaptic-subreport-widget
                                                report-title={details.title}
                                                language={this.props.deps.language}
                                                report-subject={subject?.name}
                                            />
                                        </a>
                                    </li>
                                )
                            })}
                        </ul>
                    </>
                )
            case 'section':
                // We should filter section elements out
                throw new Error('Unhandled answer type')
            case 'select':
                return (
                    <div className="AppCore__report__ViewAnswer__Select">
                        {answerValue.value.map((answer) => (
                            <div key={answer} className="AppCore__report__ViewAnswer__SelectOption">
                                {answer}
                            </div>
                        ))}
                    </div>
                )
            case 'string':
                return <div className="AppCore__report__ViewAnswer__Text">{answerValue.value}</div>
            case 'units':
                const { value } = answerValue
                if (value === 'no-units-available') {
                    return <div>{i18next.t('appcore:ViewReport.noUnitsAvailable')}</div>
                }

                const units = this.props.units.filter((unit) => value.some((id) => id === unit.id))

                return (
                    <div className="AppCore__report__ViewAnswer__Units">
                        <UnitChips
                            deps={this.props.deps}
                            units={units}
                            renderCachedImage={this.props.renderCachedImage}
                        />
                    </div>
                )
            default:
                assumeExhaustion(answerValue)
        }
    }

    private renderElementContent = (item: ElementFlowItem) => {
        const { element } = item

        switch (element.type) {
            case 'text-content':
                return TextContent(element, this.props.unitData, this.props.deps.language)
            default:
                assumeExhaustion(element.type)
        }
    }

    private renderContent = () => {
        const { item } = this.props

        switch (item.type) {
            case 'answer':
                return this.renderAnswerContent(item)
            case 'element':
                return this.renderElementContent(item)
            default:
                assumeExhaustion(item)
        }
    }

    private renderAnswerLabel = (item: AnswerFlowItem) => {
        if (item.answer.dynamicElement) {
            if (item.answer.value.type === 'units') {
                const answerValue = item.answer.value.value
                if (!isUnitArray(answerValue)) return item.answer.label

                if (answerValue.length > 0) {
                    const unitTypesForUnits: string = answerValue
                        .map((unitId) => this.props.units.find((unit) => unitId === unit.id))
                        .map((knownUnit) => this.props.unitTypes.find((unitType) => knownUnit?.type === unitType.id))
                        .filter(isNotNil)
                        .map((unitType: AppUnitType) => unitType.name)
                        .join(', ')

                    return i18next.t('appcore:ElementLoop.unitToFilterBySelectLabel', {
                        unitTypeName: unitTypesForUnits,
                    })
                } else {
                    // the user selected to not filter by units but we still need to show which unit type they did this for in the label
                    const unitTypesForUnits = item.answer.unitTypeIds
                        .map((unitId) => this.props.unitTypes.find((unitType) => unitId === unitType.id))
                        .filter(isNotNil)
                        .map((unitType: AppUnitType) => unitType.name)
                        .join(',')

                    return i18next.t('appcore:ElementLoop.unitToFilterBySelectLabel', {
                        unitTypeName: unitTypesForUnits,
                    })
                }
            }
        }

        return item.answer.label
    }

    private renderLabel = () => {
        const { item } = this.props

        switch (item.type) {
            case 'answer':
                return this.renderAnswerLabel(item)
            case 'element':
                return item.element.label
            default:
                assumeExhaustion(item)
        }
    }

    private hasLabel = () => {
        const { item } = this.props

        switch (item.type) {
            case 'answer':
                return item.answer.label ? true : false
            case 'element':
                return item.element.label ? true : false
            default:
                assumeExhaustion(item)
        }

        return false
    }

    public render = () => {
        const { item, deps, choicesLookup, userProfiles, guideNamesLookup } = this.props

        // sections should have been filtered out already but we have a bug ZAP-4452 that I cannot replicate.
        // it is failing previous in this render because sections are getting in here. Bad data perhaps?
        if (isAnswerFlowItem(item) && item.answer.value.type === 'section') {
            return null
        }

        const answerHistory = this.getAnswerHistory()
        const answerValue = this.props.item.type === 'answer' ? this.props.item.answer.value : null

        // sections should have been filtered out already but we have a bug ZAP-4452 that I cannot replicate.
        // it is failing previous in this render because sections are getting in here. Bad data perhaps?
        if (answerValue?.type === 'section') {
            return null
        }

        const referencedElementId = elementIdReferencedByItem(this.props.item)
        const hasAnswerHistory = answerHistory.length > 0

        return (
            <div
                className="AppCore__report__ViewAnswer"
                id={referencedElementId && elementDomId(referencedElementId, item.loopPath)}
            >
                <div>{this.renderIcon()}</div>
                <div className="AppCore__report__ViewAnswer__Content">
                    <div className="AppCore__report__ViewAnswer__LabelAndHistoryContainer">
                        {this.hasLabel() && (
                            <div className="AppCore__report__ViewAnswer__Label">{this.renderLabel()}</div>
                        )}
                        {hasAnswerHistory && (
                            <div className="AppCore__report__ViewAnswer__HistoryButton">
                                <IconButton
                                    iconName="recent"
                                    size="tiny"
                                    border={false}
                                    tooltip={
                                        this.state.isHistoryShown
                                            ? i18next.t('appcore:ReportAnswer.history.hide')
                                            : i18next.t('appcore:ReportAnswer.history.show')
                                    }
                                    onClick={this.onClickHistoryButton}
                                />
                            </div>
                        )}
                    </div>
                    <div
                        className={classNames('AppCore__report__ViewAnswer__Answer', {
                            'AppCore__report__ViewAnswer__Answer--with-history': hasAnswerHistory,
                        })}
                    >
                        {this.renderContent()}
                    </div>
                    {this.state.isHistoryShown && (
                        <div className="AppCore__report__ViewAnswer__AnswerHistory">
                            <AnswerTimeline
                                locale={deps.locale}
                                guide-names-lookup={guideNamesLookup || null}
                                choices-lookup={choicesLookup}
                                language={deps.language}
                                answers={answerHistory}
                                users={userProfiles}
                                bare
                            ></AnswerTimeline>
                        </div>
                    )}
                </div>
            </div>
        )
    }
}
